import React, { useEffect, useState } from "react";

import { Turnstile } from "@marsidev/react-turnstile";
import { isMessageKey } from "common/i18n/types";
import { Link } from "react-router-dom";

import { Header } from "../components/layout/header";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { config } from "../config";
import { useAuth } from "../contexts/auth-context";
import { useTranslations } from "../hooks/use-translations";
import { apiClient } from "../lib/api";

export const ForgotPasswordPage = (): React.JSX.Element => {
  const [email, setEmail] = useState(``);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(``);
  const [success, setSuccess] = useState(``);
  const [turnstileToken, setTurnstileToken] = useState(``);
  const turnstileEnabled = config.features.turnstileProtection;
  const csrfEnabled = config.features.csrfProtection;
  const { turnstileSiteKey } = config;

  const { forgotPassword } = useAuth();
  const { translations } = useTranslations();

  useEffect(() => {
    if (csrfEnabled) {
      const loadToken = async () => {
        const data = await apiClient.fetchCsrfToken();

        apiClient.setCsrfToken(data.csrfToken);
      };

      loadToken();
    }
  }, [csrfEnabled]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(``);
    setSuccess(``);

    try {
      const response = await forgotPassword(email, turnstileToken);
      const MessageKey = response.message;
      const message = translations.auth[MessageKey] ?? translations.forgotPassword.success;

      setSuccess(message);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : translations.forgotPassword.failure;

      if (isMessageKey(errorMessage)) {
        setError(translations.auth[errorMessage]);
      } else if (errorMessage === `unexpectedError` || errorMessage.includes(`Internal server error`)) {
        setError(translations.common.serverError);
      } else if (errorMessage === `networkError` || errorMessage.includes(`Network error`)) {
        setError(translations.common.networkError);
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900">
      <Header />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">{translations.forgotPassword.title}</CardTitle>
            <CardDescription className="text-center">{translations.forgotPassword.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit}>
              {error ? (
                <div
                  className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/50 dark:text-red-400 dark:border-red-800"
                  data-testid="forgot-error"
                >
                  {error}
                  {error.includes(translations.common.serverError) ? (
                    <div className="mt-2 text-xs">
                      <span>{translations.common.supportContact} </span>
                      <a className="underline hover:no-underline" href={`mailto:${config.supportEmail}`}>
                        {config.supportEmail}
                      </a>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {success ? (
                <div
                  className="p-3 text-sm text-yellow-600 bg-yellow-50 border border-yellow-200 rounded-md dark:bg-yellow-900/50 dark:text-yellow-400 dark:border-yellow-800"
                  data-testid="forgot-success"
                >
                  {success}
                </div>
              ) : null}

              <Input
                required
                data-testid="forgot-email-input"
                label={translations.common.emailLabel}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={translations.common.emailPlaceholder}
                type="email"
                value={email}
              />

              {turnstileEnabled ? <Turnstile onSuccess={setTurnstileToken} siteKey={turnstileSiteKey} /> : null}

              <Button
                className="w-full"
                data-testid="forgot-submit"
                disabled={!email || (turnstileEnabled && !turnstileToken)}
                isLoading={isLoading}
                loadingText={translations.common.loading}
                type="submit"
              >
                {translations.forgotPassword.sendInstructions}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400" to="/login">
                {translations.common.backToLogin}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
